import {
	APP_LANGUAGES,
	type AppLanguage,
	changeAppLanguage,
	useTranslation
} from '@repo/i18n'
import { Check, Languages, Search } from 'lucide-react'
import { useRef, useState } from 'react'
import { cn } from '#lib/utils'
import { Button } from '#ui/button'
import {
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerNestedRoot,
	DrawerTitle,
	DrawerTrigger
} from '#ui/drawer'
import {
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger
} from '#ui/dropdown-menu'
import { InputGroup, InputGroupAddon, InputGroupInput } from '#ui/input-group'
import { ScrollArea } from '#ui/scroll-area'
import { useSidebar } from '#ui/sidebar'

function LanguagePicker() {
	const { t, i18n } = useTranslation()
	const { isMobile } = useSidebar()

	const currentLanguage =
		APP_LANGUAGES.find(
			(language) =>
				language.code === i18n.resolvedLanguage ||
				language.code === i18n.language
		) ?? APP_LANGUAGES[0]

	if (isMobile) {
		return <MobileLanguagePicker currentLanguage={currentLanguage} t={t} />
	}

	return <DesktopLanguagePicker currentLanguage={currentLanguage} t={t} />
}

interface PickerProps {
	currentLanguage: AppLanguage
	t: ReturnType<typeof useTranslation>['t']
}

/**
 * Desktop language picker — a combobox-inside-a-menu.
 *
 * We can't use base-ui's `DropdownMenuRadioItem` for the list because the menu's
 * `listNavigation` moves DOM focus to whichever item is highlighted, which steals
 * focus away from the search input. Instead we use plain `<button>` items and
 * track the highlighted row ourselves via `activeIndex` (an aria-activedescendant
 * style pattern). The input keeps focus the whole time; arrows just shift our
 * highlight state.
 */
function DesktopLanguagePicker({ currentLanguage, t }: PickerProps) {
	const [search, setSearch] = useState('')
	const [open, setOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)

	const showSearch = APP_LANGUAGES.length > 1
	const normalizedSearch = search.trim().toLowerCase()
	const filtered = normalizedSearch
		? APP_LANGUAGES.filter((language) =>
				language.label.toLowerCase().includes(normalizedSearch)
			)
		: APP_LANGUAGES

	// Reset highlight to the top whenever the search query changes.
	// Derived-state pattern (compare last-rendered value during render) — avoids a
	// useEffect that would otherwise just mirror state.
	const [lastSearch, setLastSearch] = useState(normalizedSearch)
	if (lastSearch !== normalizedSearch) {
		setLastSearch(normalizedSearch)
		setActiveIndex(0)
	}

	/**
	 * Shift the highlighted row by `delta` (clamped to bounds) and scroll the new
	 * row into view. We can call `scrollIntoView` synchronously because the list
	 * children exist in the DOM independently of `activeIndex` — only their
	 * highlight class changes on re-render, so positions are stable.
	 */
	const moveActive = (delta: number) => {
		const next = Math.min(
			Math.max(activeIndex + delta, 0),
			filtered.length - 1
		)
		setActiveIndex(next)
		const item = listRef.current?.children[next] as HTMLElement | undefined
		item?.scrollIntoView({ block: 'nearest' })
	}

	const handleSelect = (language: AppLanguage) => {
		changeAppLanguage(language.code)
		setOpen(false)
	}

	return (
		<DropdownMenuSub
			onOpenChange={(next) => {
				setOpen(next)
				if (next) {
					// base-ui's listNavigation parks focus on a menu item after the
					// sub-content mounts. The rAF delays our focus call until after
					// that runs, so the search input ends up with focus.
					requestAnimationFrame(() => inputRef.current?.focus())
				} else {
					setSearch('')
					setActiveIndex(0)
				}
			}}
			open={open}
		>
			<DropdownMenuSubTrigger>
				<Languages />
				<div>
					<span className='block'>
						{t('shared:app_sidebar.user_menu.language')}
					</span>
					<span className='block truncate text-muted-foreground! text-xs'>
						{currentLanguage.label}
					</span>
				</div>
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent className='flex max-h-96 w-56 flex-col gap-1 p-1 pb-0'>
				{showSearch ? (
					<InputGroup className='shrink-0'>
						<InputGroupAddon>
							<Search />
						</InputGroupAddon>
						<InputGroupInput
							onChange={(e) => setSearch(e.target.value)}
							// Bubble handler: own the navigation/selection keys so they
							// drive our `activeIndex` instead of base-ui's listNavigation.
							// `preventDefault` blocks caret movement; `stopPropagation`
							// keeps the menu from also reacting to the same key.
							onKeyDown={(e) => {
								if (e.key === 'ArrowDown') {
									e.preventDefault()
									e.stopPropagation()
									moveActive(1)
								} else if (e.key === 'ArrowUp') {
									e.preventDefault()
									e.stopPropagation()
									moveActive(-1)
								} else if (e.key === 'Enter') {
									const lang = filtered[activeIndex]
									if (lang) {
										e.preventDefault()
										e.stopPropagation()
										handleSelect(lang)
									}
								}
							}}
							// Capture handler: block single-character keys from reaching
							// the menu's built-in typeahead (`useTypeahead` matches on
							// item labels and would steal focus). Multi-char keys
							// (Arrow*, Enter, Escape, Tab, …) still bubble normally.
							onKeyDownCapture={(e) => {
								if (e.key.length === 1) {
									e.stopPropagation()
								}
							}}
							placeholder={t(
								'shared:app_sidebar.user_menu.language_search_placeholder'
							)}
							ref={inputRef}
							value={search}
						/>
					</InputGroup>
				) : null}
				<ScrollArea className='min-h-0 flex-1'>
					<div className='pb-1' ref={listRef}>
						{filtered.map((language, index) => {
							const isActive = index === activeIndex
							const isSelected =
								language.code === currentLanguage.code
							return (
								<button
									className={cn(
										"relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
										isSelected &&
											'bg-accent text-accent-foreground',
										isActive &&
											'bg-accent text-accent-foreground'
									)}
									key={language.code}
									onClick={() => handleSelect(language)}
									onMouseEnter={() => setActiveIndex(index)}
									type='button'
								>
									<span className='flex-1 text-left'>
										{language.label}
									</span>
									{isSelected ? (
										<Check className='absolute right-2 text-muted-foreground' />
									) : null}
								</button>
							)
						})}
						{filtered.length === 0 ? (
							<div className='py-3 text-center text-muted-foreground text-xs'>
								{t(
									'shared:app_sidebar.user_menu.language_no_results'
								)}
							</div>
						) : null}
					</div>
				</ScrollArea>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	)
}

function MobileLanguagePicker({ currentLanguage, t }: PickerProps) {
	const [search, setSearch] = useState('')
	const [initialListHeight, setInitialListHeight] = useState<number | null>(
		null
	)

	const showSearch = APP_LANGUAGES.length > 1
	const normalizedSearch = search.trim().toLowerCase()
	const filtered = normalizedSearch
		? APP_LANGUAGES.filter((language) =>
				language.label.toLowerCase().includes(normalizedSearch)
			)
		: APP_LANGUAGES

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			setSearch('')
			// Forget the captured height so the next open measures fresh content.
			setInitialListHeight(null)
		}
	}

	// Snapshot the unfiltered list's rendered height the first time it mounts.
	// Subsequent re-renders find `initialListHeight` already set and no-op.
	const captureListHeight = (el: HTMLDivElement | null) => {
		if (el && initialListHeight === null) {
			setInitialListHeight(el.offsetHeight)
		}
	}

	// Apply the snapshot only while filtering; without filter the drawer is
	// content-sized and can be smaller than the snapshot.
	const lockedListStyle =
		normalizedSearch && initialListHeight !== null
			? { minHeight: initialListHeight }
			: undefined

	return (
		<DrawerNestedRoot onOpenChange={handleOpenChange}>
			<DrawerTrigger asChild>
				<Button
					className='h-11 justify-start gap-3 px-3 font-normal'
					variant='ghost'
				>
					<Languages />
					<div className='flex flex-1 flex-col text-left'>
						<span>
							{t('shared:app_sidebar.user_menu.language')}
						</span>
						<span className='text-muted-foreground text-xs'>
							{currentLanguage.label}
						</span>
					</div>
				</Button>
			</DrawerTrigger>
			<DrawerContent>
				<DrawerHeader className='shrink-0 border-b'>
					<DrawerTitle>
						{t('shared:app_sidebar.user_menu.language')}
					</DrawerTitle>
					<DrawerDescription className='sr-only'>
						{t('shared:app_sidebar.user_menu.language')}
					</DrawerDescription>
				</DrawerHeader>
				<div className='flex min-h-0 flex-col gap-1 px-2 pt-2'>
					{showSearch ? (
						<InputGroup className='shrink-0'>
							<InputGroupAddon>
								<Search />
							</InputGroupAddon>
							<InputGroupInput
								onChange={(e) => setSearch(e.target.value)}
								placeholder={t(
									'shared:app_sidebar.user_menu.language_search_placeholder'
								)}
								value={search}
							/>
						</InputGroup>
					) : null}
					<div
						className='flex flex-col gap-1 overflow-y-auto pb-4 [&::-webkit-scrollbar]:hidden'
						ref={captureListHeight}
						style={lockedListStyle}
					>
						{filtered.map(({ code, label }) => {
							const isSelected = code === currentLanguage.code
							return (
								<DrawerClose asChild key={code}>
									<Button
										className='h-11 justify-start gap-3 px-3 font-normal'
										onClick={() => changeAppLanguage(code)}
										variant={
											isSelected ? 'secondary' : 'ghost'
										}
									>
										<span className='flex-1 text-left'>
											{label}
										</span>
										{isSelected ? (
											<Check className='size-4 text-muted-foreground' />
										) : null}
									</Button>
								</DrawerClose>
							)
						})}
						{filtered.length === 0 ? (
							<div className='py-3 text-center text-muted-foreground text-xs'>
								{t(
									'shared:app_sidebar.user_menu.language_no_results'
								)}
							</div>
						) : null}
					</div>
				</div>
			</DrawerContent>
		</DrawerNestedRoot>
	)
}

export { LanguagePicker }
